import React, { useRef } from "react";
import { Box, Flex, Text, Link, useColorModeValue } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { Link as ChakraLink } from "@chakra-ui/react";

const MapSite = () => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-40px" });

    const bg        = useColorModeValue("#0A1628", "#03111F");
    const textColor = "#E8E0D0";

    const columns = [
        {
            title: t("MapsitEntreprise"),
            items: [
                { label: t("MapsitHMP"),         href: null },
                { label: t("MapsitText"),         href: null },
                { label: t("Mapsitadressnum"),    href: null },
            ],
        },
        {
            title: t("Mapsitcontact"),
            items: [
                { label: t("Mapsitmailnum"),  href: `mailto:${t("Mapsitmailnum")}` },
                { label: t("MapsitTelnum"),   href: `tel:${t("MapsitTelnum")}` },
            ],
        },
        {
            title: t("MapsitPDS"),
            items: [
                { label: t("MapsitAccueil"),  href: "#" },
                { label: t("MapsitServices"), href: "#Services" },
                { label: t("MapsitProduits"), href: "#Produits" },
                { label: t("MapsitAdmin"),    href: "/login", isRouter: true },
            ],
        },
    ];

    return (
        <Box
            ref={ref}
            bg={bg}
            pt={{ base: 16, md: 20 }}
            pb={{ base: 10, md: 12 }}
            px={{ base: 8, md: 16 }}
            position="relative"
            overflow="hidden"
        >
            {/* Ligne accent haut */}
            <Box
                h="1px"
                bg="linear-gradient(90deg, transparent, rgba(201,168,76,0.4), rgba(139,158,110,0.3), transparent)"
                mb={16}
            />

            {/* Ambient glow */}
            <Box
                position="absolute"
                top="-80px"
                left="50%"
                transform="translateX(-50%)"
                w="600px"
                h="200px"
                bg="radial-gradient(ellipse, rgba(201,168,76,0.04) 0%, transparent 70%)"
                pointerEvents="none"
            />

            {/* ── Colonnes ── */}
            <Flex
                direction={{ base: "column", md: "row" }}
                gap={{ base: 12, md: 0 }}
                maxW="1200px"
                mx="auto"
                justify="space-between"
                mb={16}
            >
                {columns.map((col, ci) => (
                    <motion.div
                        key={ci}
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.7, delay: ci * 0.12, ease: [0.16, 1, 0.3, 1] }}
                        style={{ flex: 1, paddingRight: ci < 2 ? "4rem" : 0 }}
                    >
                        {/* Titre colonne */}
                        <Text
                            fontSize="9px"
                            letterSpacing="0.4em"
                            textTransform="uppercase"
                            color={textColor}
                            opacity={0.35}
                            fontWeight="600"
                            mb={5}
                        >
                            {col.title}
                        </Text>

                        {/* Séparateur */}
                        <motion.div
                            initial={{ scaleX: 0 }}
                            animate={isInView ? { scaleX: 1 } : {}}
                            transition={{ duration: 0.6, delay: ci * 0.12 + 0.15, ease: [0.16, 1, 0.3, 1] }}
                            style={{ originX: 0, marginBottom: "1.25rem" }}
                        >
                            <Box
                                w="28px"
                                h="1px"
                                bg={ci === 0 ? "#C9A84C" : ci === 1 ? "#8B9E6E" : "#7BA3C0"}
                                opacity={0.6}
                            />
                        </motion.div>

                        {/* Items */}
                        <Flex direction="column" gap={3}>
                            {col.items.map((item, ii) => (
                                <motion.div
                                    key={ii}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                                    transition={{
                                        duration: 0.5,
                                        delay: ci * 0.12 + 0.25 + ii * 0.07,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                >
                                    {item.href ? (
                                        item.isRouter ? (
                                            <ChakraLink
                                                as={RouterLink}
                                                to={item.href}
                                                style={{ textDecoration: "none" }}
                                            >
                                                <Text
                                                    fontSize="sm"
                                                    color={textColor}
                                                    opacity={0.45}
                                                    fontWeight="300"
                                                    letterSpacing="0.02em"
                                                    transition="opacity 0.3s"
                                                    _hover={{ opacity: 0.85 }}
                                                    cursor="pointer"
                                                >
                                                    {item.label}
                                                </Text>
                                            </ChakraLink>
                                        ) : (
                                            <Link
                                                href={item.href}
                                                style={{ textDecoration: "none" }}
                                            >
                                                <Text
                                                    fontSize="sm"
                                                    color={textColor}
                                                    opacity={0.45}
                                                    fontWeight="300"
                                                    letterSpacing="0.02em"
                                                    transition="opacity 0.3s"
                                                    _hover={{ opacity: 0.85 }}
                                                >
                                                    {item.label}
                                                </Text>
                                            </Link>
                                        )
                                    ) : (
                                        <Text
                                            fontSize="sm"
                                            color={textColor}
                                            opacity={0.45}
                                            fontWeight="300"
                                            letterSpacing="0.02em"
                                        >
                                            {item.label}
                                        </Text>
                                    )}
                                </motion.div>
                            ))}
                        </Flex>
                    </motion.div>
                ))}
            </Flex>

            {/* ── Bas : copyright + ligne ── */}
            <Box
                h="1px"
                bg={`rgba(255,255,255,0.06)`}
                mb={8}
                maxW="1200px"
                mx="auto"
            />

            <Flex
                maxW="1200px"
                mx="auto"
                justify="space-between"
                align="center"
                direction={{ base: "column", md: "row" }}
                gap={3}
            >
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ duration: 0.8, delay: 0.6 }}
                >
                    <Text
                        fontSize="10px"
                        letterSpacing="0.2em"
                        color={textColor}
                        opacity={0.2}
                        fontWeight="400"
                    >
                        © {new Date().getFullYear()} {t("MapsitHMP")} — Tous droits réservés
                    </Text>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ duration: 0.8, delay: 0.7 }}
                >
                    <Flex align="center" gap={2}>
                        <Box w="4px" h="4px" borderRadius="full" bg="#C9A84C" opacity={0.5} />
                        <Text
                            fontSize="10px"
                            letterSpacing="0.15em"
                            color={textColor}
                            opacity={0.2}
                            fontWeight="400"
                        >
                            GROUPE HML
                        </Text>
                        <Box w="4px" h="4px" borderRadius="full" bg="#8B9E6E" opacity={0.5} />
                    </Flex>
                </motion.div>
            </Flex>
        </Box>
    );
};

export default MapSite;